import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/lib/lang";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import Reveal from "@/components/ui-brand/Reveal";
import PeonyMark from "@/components/ui-brand/PeonyMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DokumentePage = () => {
  const { lang } = useLanguage();
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [content, setContent] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: "Meine Dokumente · patrice.studio",
    description: "Persönliche Notizen, LinkedIn-Texte, Handoff-Dokumente.",
  });

  useEffect(() => {
    axios.get(`${API}/docs`).then((res) => {
      setDocs(res.data.docs || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const open = async (slug) => {
    setActive(slug);
    setContent("Lade …");
    try {
      const res = await axios.get(`${API}/docs/${slug}`);
      setContent(typeof res.data === "string" ? res.data : JSON.stringify(res.data));
    } catch {
      setContent("Konnte das Dokument nicht laden.");
    }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
      const ta = document.querySelector("[data-testid=doc-content]");
      if (ta) {
        ta.select();
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    }
  };

  return (
    <section className="relative px-6 md:px-12 lg:px-24 pt-12 md:pt-20 pb-32 overflow-hidden">
      <PeonyMark size={560} opacity={0.04} className="absolute -right-32 -top-24 pointer-events-none" />

      <Reveal>
        <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere">
          — Persönliche Dokumente
        </p>
        <h1 className="font-display text-[clamp(2.4rem,6vw,5rem)] leading-[0.96] mt-8 tracking-tight">
          <span className="italic-serif text-atmosphere">Texte,</span> zum Mitnehmen.
        </h1>
        <p className="body-lux mt-10 max-w-2xl">
          {lang === "de"
            ? 'Alle Texte, die Emmi für dich geschrieben hat — LinkedIn, Handoff-Dokumente, später vielleicht mehr. Klick auf ein Dokument, scroll nach unten, dann „Alles kopieren". Fertig für LinkedIn, Word, oder wohin du willst.'
            : "All the texts Emmi has written for you — LinkedIn, handoff docs, more to come."}
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-12 gap-6 lg:gap-10">
        {/* Doc list */}
        <Reveal className="col-span-12 lg:col-span-4">
          <div className="space-y-3">
            {loading && (
              <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere">
                Lade …
              </p>
            )}
            {!loading && docs.length === 0 && (
              <p className="font-light text-depth/80">Noch keine Dokumente.</p>
            )}
            {docs.map((d) => (
              <button
                key={d.slug}
                type="button"
                data-testid={`doc-item-${d.slug}`}
                onClick={() => open(d.slug)}
                className={`group block w-full text-left border p-5 transition-colors ${
                  active === d.slug
                    ? "border-depth bg-depth/5"
                    : "border-warmth/30 hover:border-depth"
                }`}
              >
                <p className="font-mono text-[0.58rem] tracking-[0.32em] uppercase text-atmosphere">
                  {d.slug}
                </p>
                <p className="font-display text-lg md:text-xl text-depth mt-2">
                  {d.title}
                </p>
                <p className="font-mono text-[0.58rem] tracking-[0.22em] uppercase text-atmosphere/70 mt-3">
                  {Math.round(d.size / 1024)} KB · Markdown
                </p>
              </button>
            ))}
          </div>
        </Reveal>

        {/* Doc content */}
        <Reveal delay={120} className="col-span-12 lg:col-span-8">
          {!active ? (
            <div className="border border-warmth/30 p-10 min-h-[400px] flex items-center justify-center text-center">
              <p className="font-display italic text-atmosphere/80 text-xl">
                Wähle links ein Dokument aus.
              </p>
            </div>
          ) : (
            <div className="border border-warmth/30">
              <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-warmth/30 bg-warmth/10">
                <p className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-depth">
                  {docs.find((d) => d.slug === active)?.title || active}
                </p>
                <button
                  type="button"
                  onClick={copyAll}
                  data-testid="doc-copy-all"
                  className="font-mono text-[0.62rem] tracking-[0.32em] uppercase px-4 py-2 bg-depth text-canvas hover:bg-signal transition-colors"
                >
                  {copied ? "Kopiert ✓" : "Alles kopieren ↗"}
                </button>
              </div>
              <textarea
                data-testid="doc-content"
                readOnly
                value={content}
                className="block w-full p-6 font-mono text-[0.78rem] leading-relaxed text-depth bg-canvas resize-none focus:outline-none"
                style={{ minHeight: "560px", whiteSpace: "pre-wrap" }}
                onClick={(e) => e.target.select()}
              />
            </div>
          )}
        </Reveal>
      </div>

      <Reveal delay={240}>
        <p className="mt-20 max-w-2xl font-display italic text-atmosphere/80 text-lg leading-snug">
          {lang === "de"
            ? "Tipp: Diese Seite ist nicht öffentlich verlinkt und wird nicht in Google indexiert. Du erreichst sie nur direkt über /dokumente."
            : "Tip: This page is not publicly linked and is not indexed by Google."}
        </p>
      </Reveal>
    </section>
  );
};

export default DokumentePage;
